import React, { useEffect, useState } from 'react';
import SportRoleScreen from './SportRoleScreen';

interface Sport {
  id: number;
  name: string;
  description?: string;
  members?: number[];
}

interface SportManagementProps {
  apiBaseUrl: string;
  accessToken: string | null;
  refreshKey?: any;
  onSelectSport?: (sportId: number) => void;
  userRoles?: string[];
  userSports?: string[];
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

interface SportForm {
  id: number | null;
  name: string;
  description: string;
}

const SportManagement: React.FC<SportManagementProps> = ({ apiBaseUrl, accessToken, refreshKey, onSelectSport, userRoles, userSports: propUserSports }) => {
    // Handle add/edit sport form submit
    const handleFormSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError('');
      setSaving(true);
      try {
        const method = form.id ? 'PUT' : 'POST';
        const url = form.id
          ? `${apiBaseUrl}/sports/${form.id}`
          : `${apiBaseUrl}/sports`;
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ name: form.name, description: form.description }),
        });
        if (response.ok) {
          setShowForm(false);
          setForm({ id: null, name: '', description: '' });
          fetchSports();
        } else {
          const data = await response.json();
          setFormError(data.message || 'Failed to save sport');
        }
      } catch (err) {
        setFormError('Network error or API is not running.');
      } finally {
        setSaving(false);
      }
    };
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showForm, setShowForm] = useState<boolean>(false);
  const [form, setForm] = useState<SportForm>({ id: null, name: '', description: '' });
  const [formError, setFormError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [roles, setRoles] = useState<{ id: number; role_name: string }[]>([]);
  const [roleInput, setRoleInput] = useState('');
  const [roleError, setRoleError] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [showRoleScreen, setShowRoleScreen] = useState<{ sportId: number | null }>({ sportId: null });

  // Determine which sports the current user can manage
  const userCanManageSport = (sport: Sport): boolean => {
    if (hasRole(userRoles, 'admin')) return true;
    const sportsNames = propUserSports || getUserSports(userRoles);
    return sportsNames.some(s => s.toLowerCase() === sport.name.toLowerCase());
  };

  // Only admins can add sports
  const isAdmin = hasRole(userRoles, 'admin');

  const fetchSports = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${apiBaseUrl}/sports`, {
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });
      const data = await response.json();
      if (response.ok) {
        setSports(data);
      } else {
        setError(data.message || 'Failed to fetch sports');
      }
    } catch (err) {
      setError('Network error or API is not running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSports(); }, [refreshKey]);

  const handleEdit = (sport: Sport): void => {
    setForm({ id: sport.id, name: sport.name, description: sport.description || '' });
    setShowForm(true);
    setFormError('');
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!window.confirm('Delete this sport?')) return;
    try {
      const response = await fetch(`${apiBaseUrl}/sports/${id}`, {
        method: 'DELETE',
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });
      if (response.ok) {
        // Refresh the sports list after deletion
        fetchSports();
      } else {
        setError('Failed to delete sport');
      }
    } catch (err) {
      setError('Network error or API is not running.');
    }
  };

  // Add role
  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/${form.id}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ role_name: roleInput }),
      });
      if (response.ok) {
        setRoleInput('');
        // Refresh roles
        const data = await response.json();
        fetch(`${apiBaseUrl}/sports/${form.id}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(data));
      } else {
        setRoleError('Failed to add role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  // Edit role
  const handleEditRole = async (roleId: number, newName: string) => {
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ role_name: newName }),
      });
      if (response.ok) {
        // Refresh roles
        fetch(`${apiBaseUrl}/sports/${form.id}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(data));
      } else {
        setRoleError('Failed to edit role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId: number) => {
    setRoleError('');
    setRoleSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/sports/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });
      if (response.ok) {
        // Refresh roles
        fetch(`${apiBaseUrl}/sports/${form.id}/roles`, {
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
        })
          .then(res => res.json())
          .then(data => setRoles(data));
      } else {
        setRoleError('Failed to delete role');
      }
    } catch {
      setRoleError('Network error');
    } finally {
      setRoleSaving(false);
    }
  };

  return (
    <div className="py-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Sport Management</h2>
      {showRoleScreen.sportId !== null ? (
        <SportRoleScreen
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          userRoles={userRoles}
          userSports={propUserSports}
        />
      ) : (
        <>
          {loading ? (
            <div>Loading sports...</div>
          ) : error ? (
            <div className="text-red-600">{error}</div>
          ) : (
            <>
              {isAdmin && (
                <button
                  className="mb-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                  onClick={() => { setShowForm(true); setForm({ id: null, name: '', description: '' }); setFormError(''); }}
                >
                  Add Sport
                </button>
              )}
              <table className="min-w-full bg-white border border-gray-200 mb-6">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="py-2 px-4 text-left">Name</th>
                    <th className="py-2 px-4 text-left">Description</th>
                    <th className="py-2 px-4 text-left"># Members</th>
                    <th className="py-2 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sports
                    .filter(sport => userCanManageSport(sport))
                    .map(sport => (
                    <tr key={sport.id} className="hover:bg-gray-50">
                      <td className="py-2 px-4">{sport.name}</td>
                      <td className="py-2 px-4">{sport.description}</td>
                      <td className="py-2 px-4">{sport.members ? sport.members.length : 0}</td>
                      <td className="py-2 px-4">
                        <button
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-md mr-2"
                          onClick={() => handleEdit(sport)}
                        >Edit</button>
                        {(!sport.members || sport.members.length === 0) && (
                          <button
                            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-md"
                            onClick={() => handleDelete(sport.id)}
                          >Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {showForm && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <form onSubmit={handleFormSubmit} className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md relative">
                <h3 className="text-xl font-bold mb-4">{form.id ? 'Edit Sport' : 'Add Sport'}</h3>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Name</label>
                  <input
                    type="text"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                  <input
                    type="text"
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                {/* Sport Roles Management */}
                {form.id && (
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Roles</label>
                    <ul className="mb-2">
                      {roles.map(role => (
                        <li key={role.id} className="flex items-center gap-2 mb-1">
                          <input
                            type="text"
                            className="border rounded px-2 py-1 text-sm"
                            defaultValue={role.role_name}
                            onBlur={e => {
                              if (e.target.value !== role.role_name) handleEditRole(role.id, e.target.value);
                            }}
                            disabled={roleSaving}
                          />
                          <button
                            type="button"
                            className="bg-red-500 hover:bg-red-700 text-white text-xs px-2 py-1 rounded"
                            onClick={() => handleDeleteRole(role.id)}
                            disabled={roleSaving}
                          >Delete</button>
                        </li>
                      ))}
                    </ul>
                    <form onSubmit={handleAddRole} className="flex gap-2">
                      <input
                        type="text"
                        className="border rounded px-2 py-1 text-sm"
                        value={roleInput}
                        onChange={e => setRoleInput(e.target.value)}
                        placeholder="Add new role"
                        disabled={roleSaving}
                      />
                      <button
                        type="submit"
                        className="bg-green-500 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
                        disabled={roleSaving || !roleInput.trim()}
                      >Add</button>
                    </form>
                    {roleError && <div className="text-red-600 text-xs mt-2">{roleError}</div>}
                  </div>
                )}
                {formError && <div className="mb-4 text-red-600">{formError}</div>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md"
                    onClick={() => setShowForm(false)}
                    disabled={saving}
                  >Cancel</button>
                  <button
                    type="submit"
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                    disabled={saving}
                  >{saving ? 'Saving...' : (form.id ? 'Update' : 'Add')}</button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SportManagement;
